/**
 * A pasted Google Sheets link is **input to a parser, never a fetch
 * destination**.
 *
 * This is the whole of the SSRF position, and it is stronger than the usual
 * one. "The server reads the URL from the workspace row rather than the request
 * body" is necessary and not sufficient: somebody who may configure a data
 * source can save a hostile URL first, and the server then fetches it on their
 * behalf — against the metadata endpoint, against an internal service, against
 * another tenant's host.
 *
 * So no URL is ever stored, and no stored string is ever requested. A pasted
 * link is taken apart here; only a **spreadsheet id** and a **tab id** survive,
 * each from an alphabet that cannot express a scheme, a host, a path or a
 * redirect. `buildSheetEndpoint()` then constructs the address from those parts
 * and a constant — and re-validates them as it does, so that even a row written
 * by some future bug cannot become an arbitrary request.
 *
 * Pure, and in the domain package on purpose: the screen parses to give
 * immediate feedback, the server parses because the screen is not an authority,
 * and neither may disagree with the other about what a link means.
 */

/**
 * The only hosts a sheet link may name. Matched **exactly**, never by suffix:
 * `docs.google.com.evil.tld` is a different host that ends with the same text,
 * and suffix matching is how that becomes an allow-list bypass.
 */
export const SHEET_HOSTS: readonly string[] = ['docs.google.com', 'spreadsheets.google.com'];

/** Google's own id alphabet. Nothing else is representable. */
const SPREADSHEET_ID = /^[A-Za-z0-9_-]{20,120}$/;
const GID = /^[0-9]{1,20}$/;

/**
 * How a sheet is reached. `api` is a sheet chosen through Google with the
 * company's own grant (08 §14): it has **no public endpoint**, and the builder
 * below refuses to make one, so it can never be read by the public path.
 */
export type SheetConnectionType = 'export_csv' | 'published_csv' | 'api';

export interface SheetSource {
    spreadsheetId: string;
    /** The tab. Null means "whichever tab the sheet opens on". */
    gid: string | null;
    /**
     * `published_csv` is a `/d/e/…/pub` link — File ▸ Share ▸ Publish to web.
     * `export_csv` is an ordinary document shared as "anyone with the link".
     * They are different endpoints, and a link of one kind will not load as the
     * other, so which one it is has to be remembered rather than guessed later.
     */
    connectionType: SheetConnectionType;
}

export type SheetLinkProblem =
    | 'empty'
    | 'not-a-link'
    | 'not-https'
    | 'wrong-host'
    | 'has-credentials'
    | 'has-port'
    | 'no-spreadsheet-id'
    | 'bad-gid';

export type SheetLinkResult =
    | { ok: true; source: SheetSource }
    | { ok: false; problem: SheetLinkProblem; message: string };

/**
 * The refusals, in the words the person pasting the link needs.
 *
 * None of them repeats the input back. A link is pasted from somewhere, and
 * echoing it into a page or a log is how a token in a query string ends up
 * somewhere it was not meant to be.
 */
const MESSAGES: Record<SheetLinkProblem, string> = {
    'empty': 'Paste the link to your Google Sheet.',
    'not-a-link': 'That does not look like a Google Sheets link. Copy the address from your browser’s address bar.',
    'not-https': 'The link must start with https://.',
    'wrong-host': 'That is not a Google Sheets link. It must be on docs.google.com.',
    'has-credentials': 'Remove the username and password from the link.',
    'has-port': 'Remove the port number from the link.',
    'no-spreadsheet-id': 'We could not find a sheet id in that link. Open the sheet in Google and copy the address from the address bar.',
    'bad-gid': 'The tab reference in that link is not valid. Copy the address again with the tab you want open.',
};

const refuse = (problem: SheetLinkProblem): SheetLinkResult =>
    ({ ok: false, problem, message: MESSAGES[problem] });

/**
 * A hostname, compared the way an allow-list has to compare one.
 *
 * Lower-cased because DNS is case-insensitive; one trailing dot removed because
 * `docs.google.com.` is the same host written as a fully qualified name. An
 * internationalised lookalike (Cyrillic "о" in "docs") arrives here already
 * punycoded by the URL parser, so it simply is not in the list.
 */
export function isAllowedSheetHost(hostname: string): boolean {
    const host = hostname.trim().toLowerCase().replace(/\.$/, '');
    return SHEET_HOSTS.includes(host);
}

/**
 * Take a pasted link apart, or say exactly why it cannot be used.
 *
 * Accepts a bare spreadsheet id too, because people copy that as often as they
 * copy the address, and an id is the safest thing anybody can hand us.
 */
export function parseSheetLink(raw: string | null | undefined): SheetLinkResult {
    const value = (raw ?? '').trim();
    if (!value) return refuse('empty');
    // A link long enough to be an attack on the parser is not a link.
    if (value.length > 2048) return refuse('not-a-link');

    // A bare id, which is what most people paste when asked for "the sheet id".
    if (SPREADSHEET_ID.test(value)) {
        return { ok: true, source: { spreadsheetId: value, gid: null, connectionType: 'export_csv' } };
    }

    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return refuse('not-a-link');
    }

    // Order matters only for the message. Every one of these is a refusal.
    if (url.protocol !== 'https:') return refuse('not-https');
    if (url.username || url.password) return refuse('has-credentials');
    if (url.port) return refuse('has-port');
    if (!isAllowedSheetHost(url.hostname)) return refuse('wrong-host');

    // `/spreadsheets/d/e/<id>/pub…` is a published sheet; `/spreadsheets/d/<id>/…`
    // is an ordinary document. Anchored at the start of the path, so a path that
    // merely *contains* the text — `/redirect?to=/spreadsheets/d/…` — does not
    // match.
    const published = /^\/spreadsheets\/d\/e\/([A-Za-z0-9_-]+)(?:\/|$)/.exec(url.pathname);
    const document = /^\/spreadsheets\/d\/([A-Za-z0-9_-]+)(?:\/|$)/.exec(url.pathname);

    const match = published ?? document;
    if (!match) return refuse('no-spreadsheet-id');

    const spreadsheetId = match[1]!;
    if (!SPREADSHEET_ID.test(spreadsheetId)) return refuse('no-spreadsheet-id');

    // Google puts the tab in the fragment; some copies carry it in the query.
    const fromHash = /(?:^|[#&])gid=([^&]*)/.exec(url.hash);
    const fromQuery = url.searchParams.get('gid');
    const rawGid = fromHash?.[1] ?? fromQuery;

    let gid: string | null = null;
    if (rawGid !== null && rawGid !== undefined && rawGid !== '') {
        if (!GID.test(rawGid)) return refuse('bad-gid');
        gid = rawGid;
    }

    return {
        ok: true,
        source: {
            spreadsheetId,
            gid,
            connectionType: published ? 'published_csv' : 'export_csv',
        },
    };
}

/**
 * The address the server will actually request, built from parts.
 *
 * Every component is either a constant or has just been re-validated against
 * the same alphabet the parser used. Nothing that arrived as a string is
 * interpolated without that check — which is what makes this safe to call on a
 * row read back out of the database months later, by which time nobody
 * remembers what wrote it.
 *
 * It throws rather than returning an error, because there is no caller for whom
 * "the stored configuration is not a sheet id" is an ordinary outcome: it means
 * something put a value there that the parser would never have produced.
 */
export function buildSheetEndpoint(source: SheetSource): string {
    if (!SPREADSHEET_ID.test(source.spreadsheetId)) {
        throw new Error('refusing to build a sheet endpoint: the stored spreadsheet id is not one');
    }
    if (source.gid !== null && !GID.test(source.gid)) {
        throw new Error('refusing to build a sheet endpoint: the stored tab id is not one');
    }

    if (source.connectionType === 'api') {
        // Never a fallback: a sheet chosen through Google is read with the
        // company's grant or not at all.
        throw new Error('refusing to build a public endpoint for a sheet connected through Google');
    }

    const tab = source.gid === null ? '' : `&gid=${source.gid}`;

    return source.connectionType === 'published_csv'
        ? `https://docs.google.com/spreadsheets/d/e/${source.spreadsheetId}/pub?output=csv${tab}`
        : `https://docs.google.com/spreadsheets/d/${source.spreadsheetId}/export?format=csv${tab}`;
}

/**
 * Where a person should click to open the sheet themselves.
 *
 * Shown only to somebody who may manage the source (08 §6). Built the same way
 * and from the same validated parts, so the "open in Google" link cannot become
 * a way to smuggle a different address onto the page.
 */
export function sheetViewUrl(source: SheetSource): string {
    if (!SPREADSHEET_ID.test(source.spreadsheetId)) {
        throw new Error('refusing to build a sheet link: the stored spreadsheet id is not one');
    }
    const tab = source.gid === null ? '' : `#gid=${source.gid}`;
    return source.connectionType === 'published_csv'
        ? `https://docs.google.com/spreadsheets/d/e/${source.spreadsheetId}/pubhtml${tab}`
        : `https://docs.google.com/spreadsheets/d/${source.spreadsheetId}/edit${tab}`;
}

/** The last four characters of an id, for a screen that must not show the whole one. */
export const sheetIdHint = (spreadsheetId: string): string =>
    spreadsheetId.length <= 8 ? spreadsheetId : `…${spreadsheetId.slice(-6)}`;
